/**
 * Centralized Color Theme
 * All colors used throughout the application
 */

const colors = {
  // Primary Brand Colors
  primary: {
    main: '#4CC0AD', // Main brand color (teal)
    light: '#76C7B6',
    dark: '#3AA896',
    contrast: '#FFFFFF',
  },

  // Secondary Brand Colors
  secondary: {
    main: '#F4B82E', // Yellow/Gold
    light: '#F6C55E',
    dark: '#E0A615',
    contrast: '#000000',
  },

  // Blue Tones
  // Blue Tones
  blue: {
    main: '#1976D2',
    light: '#42A5F5',
    dark: '#1565C0',
    lighter: '#E3F2FD',
    border: '#D6E4FF',
    background: '#EEF5FF',
    primary: '#007bff', // Bootstrap Primary
    action: '#4299e1', // Tailwind Blue 400
    darkText: '#2c5282', // Tailwind Blue 800
    lightBg: '#ebf8ff', // Tailwind Blue 50
  },

  // Green Tones (Success)
  green: {
    main: '#2E7D32',
    light: '#388E3C',
    dark: '#1B5E20',
    lighter: '#E9F9EF',
    border: '#C6F2D3',
    success: '#28A745',
    bright: '#22c55e', // Tailwind Green 500
  },

  // Red Tones (Error/Danger)
  red: {
    main: '#D32F2F',
    light: '#F44336',
    dark: '#C62828',
    lighter: '#FFEBEE',
    border: '#FFCDD2',
    danger: '#dc3545', // Bootstrap Danger
    alert: '#ef4444', // Tailwind Red 500
    darkAlert: '#e53e3e', // Tailwind Red 600
    darkerAlert: '#c53030', // Tailwind Red 700
    lightBg: '#fff5f5', // Tailwind Red 50
    lightBorder: '#fc8181', // Tailwind Red 300
  },

  // Orange Tones (Warning)
  orange: {
    main: '#F57C00',
    light: '#FF9800',
    dark: '#E65100',
    warning: '#ED6C02',
    alert: '#ed8936', // Tailwind Orange 400
    darkAlert: '#dd6b20', // Tailwind Orange 600
    lightBg: '#fffaf0', // Tailwind Orange 50
    brand: '#EE7219',
  },

  // Grey Scale
  grey: {
    50: '#FAFAFA', // Original
    100: '#F8F8F8', // Original
    200: '#F2F2F2', // Original
    300: '#E2E2EE', // Original
    400: '#C6C5DE', // Original
    500: '#9E9E9E', // Original
    600: '#757575', // Original
    700: '#666666', // Original
    800: '#333333', // Original
    900: '#212529', // Original
    // Expanded Greys (Tailwind mappings)
    t50: '#f7fafc',
    t200: '#e2e8f0',
    t300: '#cbd5e0',
    t400: '#a0aec0',
    t500: '#718096',
    t700: '#2d3748',
    material400: '#bdbdbd',
  },

  // Background Colors
  background: {
    default: '#F3F3F8', // rgb(243, 243, 248)
    paper: '#FFFFFF',
    secondary: '#F8F9FA',
    disabled: '#FAFAFA',
    overlay: 'rgba(255, 255, 255, 0.2)',
    lightGrey: '#f6f6f9', // From SCSS
    light: '#f0f0f0',
    white: '#FFFFFF',
  },

  // Text Colors
  text: {
    primary: '#212529', // rgb(33, 37, 41)
    secondary: '#666666',
    disabled: '#9E9E9E',
    hint: '#757575',
    white: '#FFFFFF',
  },

  // Border Colors
  border: {
    main: '#E2E2EE',
    light: '#F2F2F2',
    medium: '#C6C5DE',
    input: '#C6C5DE',
    divider: '#DDD',
    grey: '#CCC',
    modal: '#D8D2D2FF',
  },

  // Status Colors
  status: {
    pending: '#ED6C02',
    started: '#1976D2',
    success: '#2E7D32',
    failure: '#D32F2F',
    info: '#0288D1',
    warning: '#FDB022', // New
    error: '#F04438', // New
    successAlt: '#12B76A', // New
  },

  // Additional Palettes
  teal: {
    main: '#2dd4bf',
  },
  yellow: {
    main: '#facc15',
  },
  slate: {
    main: '#526269',
    dark: '#475569',
  },
  brand: {
    medium: '#2b3946',
    dark: '#08202A',
    darker: '#1A2A38',
  },

  // Component Specific Colors
  button: {
    primary: '#4CC0AD', // rgb(76, 192, 173)
    primaryHover: '#3AA896',
    secondary: '#0056B3',
    error: '#D32F2F',
  },

  // Sidebar/Navigation
  sidebar: {
    background: '#FFFFFF',
    border: '#E2E2EE',
    itemBorder: '#F2F2F2',
    text: '#333333',
  },

  // Header/Top Navigation
  header: {
    background: '#FFFFFF',
    border: '#E2E2EE',
    text: '#212529',
  },

  // Footer
  footer: {
    background: '#F8F8F8',
    border: '#E7E7E7',
    text: '#333333',
    separator: '#999999',
  },

  // Table
  table: {
    headerBackground: '#FFFFFF',
    rowBackground: '#FFFFFF',
    alternateRowBackground: '#FAFAFA',
    border: '#E0E0E0',
    categoryHeader: '#E3F2FD',
  },

  // Analysis Sections (for comparison/tender analysis)
  analysis: {
    pricing: '#F57C00',
    scope: '#1976D2',
    strengths: '#2E7D32',
    redFlags: '#D32F2F',
  },

  // Palette for distinguishing grouped subcontractors (up to 8 distinct colors)
  quote: {
    subcontractorGroup: [
      '#4299e1', // blue
      '#48bb78', // green
      '#ed8936', // orange
      '#9f7aea', // purple
      '#f56565', // red
      '#38b2ac', // teal
      '#ecc94b', // yellow
      '#667eea', // indigo
    ],
  },

  // Transparent/Special
  transparent: 'transparent',
  inherit: 'inherit',
  whiteAlpha: {
    30: 'rgba(255, 255, 255, 0.3)',
  },
  blackAlpha: {
    20: 'rgba(0, 0, 0, 0.2)',
  },
  shadows: {
    yellow: 'rgba(244, 184, 46, 0.35)', // Based on secondary.main
  },
};

export default colors;
